import { useEffect, useRef, type JSX } from 'react';

import { Box, IconButton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';

import type { JizdaListItem } from '@/api/types/Jizda';
import { BrandChip } from '@/components/shell';
import { useAppSelector } from '@/store/hooks';
import { selectIsMine } from '@/store/userSlice';
import { selectAssignedUserName } from '@/store/usersSlice';
import { formatDateCs, formatTimeCs, formatWeekdayCs } from '@/utils/date';

import { CarrierTypeCaption } from './carrierType';
import { formatSpzForDisplay } from './spzFormat';
import { STORNO_ROW_TINT_ALPHA, StornoChip } from './StornoChip';

// **SEPARATE component tree from desktop** per NF-05 / UI-02.1 / ADR-0029.
// NOT a CSS-collapsed responsive table. CHR-02: flat jízda fields (no
// loadings sub-list, no count/qty, no RULE-02), card layout otherwise
// unchanged.

const CARD_PADDING_FACTOR = 2;
const TAP_TARGET_MIN_PX = 44;
const DISPATCHER_CHIP_MAX_WIDTH = 160;

// Vapas `.oc-row .lbl` style (DESIGN.md mockup) — uppercase 11px label column.
const labelSx = {
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  fontWeight: 600,
  minWidth: 80,
};

const DispatcherChip = ({ sub }: { sub: string | null }): JSX.Element => {
  const { t } = useTranslation('orders');
  const resolvedName = useAppSelector(selectAssignedUserName(sub));
  // My-jizdy highlight: yellow (`mine`) dispatcher chip when this card is
  // assigned to the logged-in user — single shared selector.
  const isMine = useAppSelector(selectIsMine(sub));
  if (sub === null) {
    return (
      <BrandChip variant="danger" dot>
        {t('row.dispatcherUnassigned')}
      </BrandChip>
    );
  }
  return (
    <Tooltip title={resolvedName} placement="top">
      <span>
        <BrandChip
          variant={isMine ? 'mine' : 'brand'}
          dot
          maxWidth={DISPATCHER_CHIP_MAX_WIDTH}
        >
          {resolvedName}
        </BrandChip>
      </span>
    </Tooltip>
  );
};

export interface OrdersCardsProps {
  orders: JizdaListItem[];
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onOpenOrder: (id: string) => void;
  onOpenHistory: (id: string) => void;
}

export const OrdersCards = ({
  orders,
  hasMore,
  loadingMore,
  onLoadMore,
  onOpenOrder,
  onOpenHistory,
}: OrdersCardsProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore();
      },
      { rootMargin: '300px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(CARD_PADDING_FACTOR),
        bgcolor: theme.palette.background.default,
      }}
      data-testid="orders-cards-root"
    >
      {orders.length === 0 && !hasMore ? (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, p: 2 }}>
          {t('list.empty')}
        </Typography>
      ) : null}

      {orders.map((order) => {
        const isDeleted = order.deleted;
        const isStorno = order.stornoState === 'STORNO';
        const dateStr = formatDateCs(order.loadingDate);
        const dayStr = formatWeekdayCs(order.loadingDate);
        const arrival = formatTimeCs(order.plannedArrival);
        // Header subline: DEN · Datum · Čas (CHR-05). Joined with separators
        // only between present parts so there is never a leading/trailing ` · `.
        const sublineStr = [dayStr, dateStr, arrival].filter(Boolean).join(' · ');
        const placesStr =
          (order.loadingPlace ?? '') +
          (order.unloadingPlace ? ` → ${order.unloadingPlace}` : '');
        const spz = formatSpzForDisplay(order.vehicle?.spz);
        const commodityName = order.commodity?.name ?? '';
        const customerName = order.customer?.name ?? '';
        // DOPRAVCE (zadání §5 / ADR-0038): VAPAS renders the literal "VAPAS"
        // label (carrier FK null); a contractor renders name + KONTRAKTOR.
        const isVapas = order.carrierType === 'VAPAS';
        const carrierName = order.carrier?.name ?? '';
        return (
          <Box
            key={order.id}
            data-testid={`order-card-${order.id}`}
            data-storno={isStorno ? 'true' : 'false'}
            onClick={() => onOpenOrder(order.id)}
            sx={{
              // STORNO (zadání §6b / ADR-0039): full-card red tint via the theme
              // `error` palette token (orthogonal to soft-delete). Separate
              // mobile tree from the desktop table — NOT a CSS-collapsed row.
              bgcolor: isStorno
                ? alpha(theme.palette.error.main, STORNO_ROW_TINT_ALPHA)
                : theme.palette.background.paper,
              border: `1px solid ${
                isStorno
                  ? alpha(theme.palette.error.main, 0.4)
                  : theme.palette.divider
              }`,
              borderRadius: 3, // 12px (DESIGN.md §4 card radius)
              p: theme.spacing(2),
              cursor: 'pointer',
              opacity: isDeleted ? 0.5 : 1,
              boxShadow: theme.elevation.shadow1,
              minHeight: TAP_TARGET_MIN_PX,
            }}
          >
            {/* Header: KOMODITA (CHR-04 — replaces Zákazník at the top) +
                date · čas + history */}
            <Stack direction="row" justifyContent="space-between" gap={2} alignItems="flex-start">
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                  <Typography
                    data-testid={`order-card-commodity-${order.id}`}
                    sx={{
                      fontWeight: 600,
                      fontSize: 15,
                      color: theme.palette.text.primary,
                      letterSpacing: '-0.01em',
                      textDecoration: isDeleted ? 'line-through' : 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {commodityName}
                  </Typography>
                  {isStorno ? <StornoChip /> : null}
                </Stack>
                <Typography
                  data-testid={`order-card-subline-${order.id}`}
                  sx={{
                    fontFamily: theme.monospaceFontFamily,
                    fontSize: 12,
                    color: theme.palette.text.secondary,
                    mt: '2px',
                  }}
                >
                  {sublineStr}
                </Typography>
              </Box>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenHistory(order.id);
                }}
                sx={{
                  width: TAP_TARGET_MIN_PX,
                  height: TAP_TARGET_MIN_PX,
                  color: theme.palette.secondary.main,
                }}
                aria-label={t('column.history')}
              >
                <HistoryIcon fontSize="small" />
                <Typography
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontFamily: theme.monospaceFontFamily,
                    fontSize: 11,
                  }}
                >
                  {order.revisionCount}
                </Typography>
              </IconButton>
            </Stack>

            {/* Route (Nakládka → Vykládka) — CHR-05: pulled out of the header
                subline onto its own standalone row in stronger text, no label.
                Omitted entirely when both places are empty. */}
            {placesStr ? (
              <Typography
                data-testid={`order-card-route-${order.id}`}
                sx={{
                  mt: 1,
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {placesStr}
              </Typography>
            ) : null}

            {/* Rows: SPZ / dopravce / dispečer — `.oc-row` dashed dividers */}
            <Stack mt={1.5}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ borderTop: `1px dashed ${theme.palette.divider}`, py: 1, fontSize: 13 }}
              >
                <Typography sx={{ ...labelSx, color: theme.palette.text.secondary }}>
                  {t('column.spz')}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: theme.monospaceFontFamily,
                    textTransform: 'uppercase',
                  }}
                  data-testid={`order-card-spz-${order.id}`}
                >
                  {spz || '—'}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ borderTop: `1px dashed ${theme.palette.divider}`, py: 1, fontSize: 13 }}
              >
                <Typography sx={{ ...labelSx, color: theme.palette.text.secondary }}>
                  {t('column.carrier')}
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  {isVapas ? (
                    <Typography sx={{ fontSize: 13, color: theme.palette.text.primary }}>
                      {t('carrierType.VAPAS')}
                    </Typography>
                  ) : carrierName ? (
                    <>
                      <Typography sx={{ fontSize: 13, color: theme.palette.text.primary }}>
                        {carrierName}
                      </Typography>
                      <CarrierTypeCaption carrierType={order.carrierType} />
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 13, color: theme.palette.text.disabled }}>
                      {t('row.carrierUnassigned')}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ borderTop: `1px dashed ${theme.palette.divider}`, py: 1, fontSize: 13 }}
              >
                <Typography sx={{ ...labelSx, color: theme.palette.text.secondary }}>
                  {t('column.dispatcher')}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <DispatcherChip sub={order.assignedDispatcherSub} />
                </Box>
              </Stack>
            </Stack>

            {/* Poznámka pro dispečera (overview note) — `.oc-loadings` slot */}
            {order.noteForDispatcherSnippet ? (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${theme.palette.divider}` }}>
                <Typography sx={{ ...labelSx, color: theme.palette.text.secondary, display: 'block', mb: 1 }}>
                  {t('column.note')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                  data-testid={`order-card-note-${order.id}`}
                >
                  {order.noteForDispatcherSnippet}
                </Typography>
              </Box>
            ) : null}

            {/* ZÁKAZNÍK (CHR-04) — moved below Poznámka as a new label-value
                row (it was the card header before commodity took the top). */}
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${theme.palette.divider}` }}>
              <Typography
                sx={{ ...labelSx, color: theme.palette.text.secondary, display: 'block', mb: 1 }}
              >
                {t('column.customer')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  textDecoration: isDeleted ? 'line-through' : 'none',
                }}
                data-testid={`order-card-customer-${order.id}`}
              >
                {customerName || '—'}
              </Typography>
            </Box>

            {/* Footer: Otevřít action — `.oc-footer` */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}
            >
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  minHeight: TAP_TARGET_MIN_PX,
                  color: theme.palette.primary.main,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {t('row.open')}
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </Stack>
            </Stack>
          </Box>
        );
      })}

      <Box
        ref={sentinelRef}
        sx={{
          p: theme.spacing(2),
          fontSize: 12,
          color: theme.palette.text.secondary,
          textAlign: 'center',
        }}
        data-testid="orders-cards-sentinel"
      >
        {hasMore ? (
          <Typography variant="caption">{t('pagination.loadingMore')}</Typography>
        ) : orders.length === 0 ? null : (
          <Typography variant="caption">{t('list.endOfList')}</Typography>
        )}
      </Box>
    </Box>
  );
};
